import { PocIcon } from 'commons/components/components-wrapper/poc-icon.component';
import { PocTag, PocTagProps } from 'commons/components/components-wrapper/poc-tag.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { TooltipedElementComponent } from 'commons/components/tooltiped-element';
import { useId } from 'commons/hooks/use-id/use-id.hook';
import type { PocInputValidity } from 'commons/types';
import { useTranslation } from 'core/i18n/i18n';
import { STATUS_TRACKING_TRANSLATION_PREFIX } from '../../constants';
import {
    DeviceConfigStatusTrackingAssistiveText,
    DeviceConfigStatusTrackingInput,
    DeviceConfigStatusTrackingRightCol,
    DeviceConfigStatusTrackingRow,
    DeviceConfigStatusTrackingRowTypography,
} from '../status-tracking-row.style';
import { AlarmTagAndText } from './status-tracking-threshold-row.style';

interface StatusTrackingThresholdRowProps {
    tagStatus: PocTagProps['status'];
    label: string;
    value: number | undefined;
    minHours: number;
    disabled: boolean;
    validity: PocInputValidity;
    onChange: (value: number) => void;
    assistiveText?: string;
    inputTestId?: string;
    suffixTestId?: string;
    assistiveTextTestId?: string;
}

export const StatusTrackingThresholdRow = ({
    tagStatus,
    label,
    value,
    minHours,
    disabled,
    validity,
    onChange,
    assistiveText,
    inputTestId,
    suffixTestId,
    assistiveTextTestId,
}: StatusTrackingThresholdRowProps) => {
    const { t } = useTranslation();
    const tooltipId = useId();

    return (
        <DeviceConfigStatusTrackingRow>
            <AlarmTagAndText>
                <PocTag status={tagStatus}>
                    <PocIcon slot="prefix" name="alarm" />
                </PocTag>
                <DeviceConfigStatusTrackingRowTypography>{label}</DeviceConfigStatusTrackingRowTypography>
            </AlarmTagAndText>
            <DeviceConfigStatusTrackingRightCol>
                <DeviceConfigStatusTrackingInput
                    aria-label={label}
                    data-testid={inputTestId}
                    value={`${value ?? ''}`}
                    variant="outlined"
                    size="small"
                    type="number"
                    min={minHours}
                    disabled={disabled}
                    validity={validity}
                    controlledValidity
                    noClearIcon
                    onValueChange={(e) => onChange(+e.detail)}
                >
                    <PocTypography variant="subtitle1" slot="suffix" data-testid={suffixTestId}>
                        {t(`${STATUS_TRACKING_TRANSLATION_PREFIX}.prefix.hours`)}
                    </PocTypography>
                    {assistiveText !== undefined && (
                        <DeviceConfigStatusTrackingAssistiveText
                            id={tooltipId}
                            data-testid={assistiveTextTestId}
                            status={validity.state === 'error' ? 'alarm' : 'inherit'}
                        >
                            <TooltipedElementComponent
                                anchorId={tooltipId}
                                hideTooltip={validity.state === 'valid'}
                                tooltipText={assistiveText}
                            />
                            {assistiveText}
                        </DeviceConfigStatusTrackingAssistiveText>
                    )}
                </DeviceConfigStatusTrackingInput>
            </DeviceConfigStatusTrackingRightCol>
        </DeviceConfigStatusTrackingRow>
    );
};
